import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Activity } from 'lucide-react';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AboutPage() {
  const navigate = useNavigate();
  const [aboutSection, setAboutSection] = useState(null);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [aboutRes, testimonialsRes] = await Promise.all([
          fetch(`${API}/about`),
          fetch(`${API}/testimonials`)
        ]);
        if (aboutRes.ok) setAboutSection(await aboutRes.json());
        if (testimonialsRes.ok) setTestimonials(await testimonialsRes.json());
      } catch (e) {
        console.error('Failed to fetch about data');
      }
    };
    load();
  }, []);

  const autoPlayRef = useCallback((node) => {
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) node.play().catch(() => {});
        else node.pause();
      },
      { threshold: 0.3 }
    );
    observer.observe(node);
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white">
      <nav className="fixed top-0 w-full z-50 backdrop-blur-md bg-[#0B0B0B]/85 border-b border-white/5">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center">
          <button
            onClick={() => navigate('/')}
            className="text-xl sm:text-2xl font-bold tracking-tight"
            style={{ fontFamily: 'Clash Display' }}
            data-testid="about-logo-home"
          >
            Move<span className="text-[#C6A97C]">More</span>
          </button>
          <Button
            data-testid="about-back-btn"
            onClick={() => navigate('/')}
            className="bg-transparent hover:bg-white/5 border border-white/20 text-white rounded-full px-4 sm:px-6 text-sm sm:text-base"
          >
            <ArrowLeft className="mr-2 w-4 h-4" /> Back to Home
          </Button>
        </div>
      </nav>

      <section className="pt-32 pb-20 px-6" data-testid="about-page-content">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="text-xs uppercase tracking-widest text-[#C6A97C] mb-4 font-semibold">About</div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6" style={{ fontFamily: 'Clash Display' }}>
                {aboutSection?.title || 'Meet Your Coach'}
              </h1>
              <p className="text-gray-300 text-lg leading-relaxed whitespace-pre-line">
                {aboutSection?.description || 'Move more with Brandon as your personal coach.'}
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden"
            >
              {aboutSection?.image_file ? (
                <img
                  data-testid="about-portrait-image"
                  src={`${API}/about/image/${aboutSection.image_file}`}
                  alt={aboutSection.title || 'About MoveMore'}
                  className="w-full aspect-[3/4] object-cover"
                />
              ) : (
                <div className="w-full aspect-[3/4] flex items-center justify-center bg-[#161616]">
                  <Activity className="w-16 h-16 text-[#C6A97C]" />
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {(aboutSection?.youtube_url || aboutSection?.video_file) && (
        <section className="pb-20 px-6" data-testid="about-video-section">
          <div className="container mx-auto max-w-3xl">
            <h2 className="text-3xl font-bold mb-6 text-center" style={{ fontFamily: 'Clash Display' }}>A Message From Me</h2>
            <div className="rounded-2xl overflow-hidden bg-black aspect-video">
              {aboutSection.youtube_url ? (
                <iframe
                  src={`https://www.youtube.com/embed/${aboutSection.youtube_url.split('v=')[1]?.split('&')[0] || aboutSection.youtube_url.split('/').pop()}`}
                  title="MoveMore Introduction"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>
              ) : (
                <video
                  ref={autoPlayRef}
                  controls
                  muted
                  playsInline
                  preload="auto"
                  poster={`${API}/videos/${aboutSection.video_file}/thumbnail`}
                  className="w-full h-full object-cover"
                  src={`${API}/about/video/${aboutSection.video_file}`}
                >
                  Your browser does not support the video tag.
                </video>
              )}
            </div>
          </div>
        </section>
      )}

      {testimonials.length > 0 && (
        <section className="pb-24 px-6" data-testid="about-testimonials-section">
          <div className="container mx-auto max-w-5xl">
            <h2 className="text-3xl font-bold mb-10 text-center" style={{ fontFamily: 'Clash Display' }}>What Clients Say</h2>
            <div className="grid md:grid-cols-2 gap-5">
              {testimonials.map((t, i) => (
                <motion.div
                  key={t.id || i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: (i % 2) * 0.08 }}
                  className="bg-white/5 border border-white/10 rounded-xl p-6"
                  data-testid={`about-testimonial-${i}`}
                >
                  <div className="flex gap-1 mb-2 text-[#C6A97C] text-sm">
                    {[...Array(t.rating || 5)].map((_, j) => <span key={j}>★</span>)}
                  </div>
                  <p className="text-gray-200 leading-relaxed mb-4">"{t.content}"</p>
                  <div className="flex items-center gap-3">
                    {t.image_url ? (
                      <img src={t.image_url} alt={t.name} className="w-9 h-9 rounded-full object-cover" />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-[#C6A97C] text-[#0B0B0B] flex items-center justify-center font-bold text-sm">
                        {t.name.charAt(0)}
                      </div>
                    )}
                    <div>
                      <div className="font-semibold text-sm">{t.name}</div>
                      <div className="text-xs text-gray-500">{t.role}</div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
